import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UsersIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ComputerDesktopIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon },
  { name: 'My Profile', path: '/profile', icon: UserIcon },
  { name: 'Attendance', path: '/attendance', icon: CalendarIcon },
  { name: 'Leave', path: '/leave', icon: ClockIcon },
  { name: 'Payroll', path: '/payroll', icon: CurrencyDollarIcon },
  { name: 'Employees', path: '/employees', icon: UsersIcon, adminOnly: true },
  { name: 'Asset Management', path: '/assets', icon: ComputerDesktopIcon, adminOnly: true },
  { name: 'Reimbursement', path: '/reimbursement', icon: BanknotesIcon, adminOnly: true },
  { name: 'Reports', path: '/reports', icon: DocumentTextIcon, adminOnly: true },
  { name: 'Settings', path: '/settings', icon: Cog6ToothIcon, adminOnly: true },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { isHR, isAdminHead, user } = useAuth();
  const location = useLocation();

  const filteredNavigation = navigation.filter(
    (item) => {
      if (!item.adminOnly) return true;
      // Show admin-only items to HR/Admin or Admin Head
      if (isHR) return true;
      // Admin Head has access to Asset Management and Reimbursement
      if (isAdminHead && (item.path === '/assets' || item.path === '/reimbursement')) return true;
      return false;
    }
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 w-64 bg-gray-900 text-white z-50
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo with close button */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-lg font-bold">H</span>
            </div>
            <span className="text-lg font-semibold">HRM System</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3 overflow-y-auto" style={{ height: 'calc(100vh - 8rem)' }}>
          <ul className="space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.path}
                    onClick={onClose}
                    className={`
                      flex items-center gap-3 px-4 py-2.5 rounded-lg
                      transition-colors duration-200
                      ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }
                    `}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold">
                {user?.employee?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.employee ? `${user.employee.firstName} ${user.employee.lastName}` : user?.email}
              </p>
              <p className="text-xs text-gray-400 capitalize">{user?.role?.toLowerCase()}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
